import { computed, inject } from '@angular/core';
import { IrisPermissionI, IrisPermissionSubjectI } from '@iris/common/models/IrisPermission';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { patchState, signalStore, type, withMethods } from '@ngrx/signals';
import { entityConfig, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, of, pipe, switchMap } from 'rxjs';
import { IrisFolderPermissionsService } from '../services/folder-permissions.service';
import { tapResponse } from '@ngrx/operators';
import { chain } from 'lodash';
import { FolderPermissionActionType, IrisDmsPermissionSubject } from '../models/IrisDMSFolderPermission';
import { HttpErrorResponse } from '@angular/common/http';

export const selectId = (entity: Partial<IrisPermissionI>): string => `${entity.userGroupId}_${entity.action}`;

const config = entityConfig({
  entity: type<IrisPermissionI>(),
  selectId,
});

export const IrisDmsFolderPermissionsStore = signalStore(
  { providedIn: 'root' },
  withEntities(config),
  withRequestState(),
  withMethods((
    store,
  ) => ({
    getFolderPermissionsByUserGroupAndAction: (userGroupId: number, action: string) => computed(() => {
      return store.entityMap()[selectId({ userGroupId, action })];
    }),
  })),
  withMethods((
    store,
    folderPermissionsService = inject(IrisFolderPermissionsService),
  ) => ({
    hasFolderPermissionByUserGroupAndAction: (userGroupId: number, action: string) => computed(() => {
      const permission = store.getFolderPermissionsByUserGroupAndAction(userGroupId, action);
      return permission()?.allowed ?? null;
    }),
    fetchFolderPermissions$: rxMethod<string>(
      pipe(
        filter(() => !store.loading()),
        switchMap((folderId: string) => {
          patchState(store, setLoading());
          return folderPermissionsService.getPermissions(folderId).pipe(
            tapResponse({
              next: permissions => {
                const mappedPermissions = chain(permissions)
                  .flatMap(({ subjects }) => subjects)
                  .filter(['name', IrisDmsPermissionSubject])
                  .flatMap((subject: IrisPermissionSubjectI) => subject.permissions)
                  .value();

                patchState(store, setAllEntities(mappedPermissions, config), setLoaded());
              },
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    deletePermission$: (permission: IrisPermissionI) => folderPermissionsService.deletePermission(permission.folderId, permission.userGroupId, permission.id),
  })),
  withMethods((
    store,
    folderPermissionsService = inject(IrisFolderPermissionsService),
  ) => ({
    toggleFolderPermission: rxMethod<{ folderId: string; userGroupId: number; action: FolderPermissionActionType; allowed: boolean }>(
      pipe(
        switchMap(({ folderId, userGroupId, action, allowed }) => {
          const permission = store.getFolderPermissionsByUserGroupAndAction(userGroupId, action);
          if (allowed === null && !permission() || permission() && allowed === permission()?.allowed) {
            return of(null);
          }
          const deletePermission = store.getFolderPermissionsByUserGroupAndAction(userGroupId, FolderPermissionActionType.Delete);
          if (allowed === null) {
            if (!permission()?.id || !deletePermission()?.id) {
              store.fetchFolderPermissions$(folderId);
              return of(null);
            }
            return folderPermissionsService.deletePermission(folderId, userGroupId, permission().id).pipe(
              // merge 'delete' into the 'update' permission status
              switchMap(removedPermission =>
                action === FolderPermissionActionType.Update
                  ? folderPermissionsService.deletePermission(folderId, userGroupId, deletePermission().id)
                  : of(removedPermission),
              ),
              tapResponse({
                next: () => store.fetchFolderPermissions$(folderId),
                error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
              }),
            );
          }
          return folderPermissionsService.setPermission(folderId, userGroupId, action, allowed).pipe(
            // merge 'delete' into the 'update' permission status
            switchMap(newPermission =>
              action === FolderPermissionActionType.Update
                ? folderPermissionsService.setPermission(folderId, userGroupId, FolderPermissionActionType.Delete, allowed)
                : of(newPermission),
            ),
            tapResponse({
              next: () => store.fetchFolderPermissions$(folderId),
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
  })),
);
